import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil } from "lucide-react";
import { createProviderFromDraft } from "../../api/providers";
import UserAuthStore from "../../store/userStore";
import UIStore from "../../store/uiStore";
import Header from "../../components/Header/Header";
import ModelsHeader from "../../components/Settings/ModelsHeader";
import { CompactButton, PrimaryButton, SecondaryButton } from "../../components/Button/Buttons";
import FormInfo from "../../components/Inputs/FormInfo";
import Input from "../../components/Inputs/Input";
import Select from "../../components/Inputs/Select";
import FileUploadInput from "../../components/Inputs/FileUploadInput";
import FileEditorSection from "../../components/Forge/FileEditorSection";
import AlertBanner from "../../components/shared/AlertBanner/AlertBanner";
import Toggle from "../../components/shared/Toggle";
import ROUTES from "../../router/routes";
import { apiUrl } from "../../config";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import useProviderDraft from "../../hooks/useProviderDraft";

const FIELD_LABEL_CLASS = "flex items-center gap-1 text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300 mb-1.5";
const HELP_TEXT_CLASS = "text-[11px] text-ink-gray-400 mt-1";
const SECTION_HEADING_CLASS = "text-sm font-semibold text-ink-gray-900 dark:text-ink-white mb-4";

const AddProviderPage = ({ settingsMode = false }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgId = UserAuthStore((state) => state.orgId);
  const orgAvailable = UIStore((state) => state.orgAvailable);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const returnRoute = settingsMode ? ROUTES.SETTINGS.FORGE_PROVIDERS : ROUTES.FORGE.ROOT;

  useEffect(() => {
    if (settingsMode && orgAvailable === false) {
      navigate(ROUTES.SETTINGS.ROOT, { replace: true });
    }
  }, [navigate, orgAvailable, settingsMode]);

  const providerDraft = useProviderDraft();

  const validationSchema = Yup.object().shape({
    provider_id: Yup.string()
      .trim()
      .matches(/^[a-z0-9_]+$/, "Only lowercase letters, numbers, and underscores allowed")
      .required("Provider ID is required"),
    name: Yup.string()
      .trim()
      .required("Display Name is required"),
    service_type: Yup.string()
      .trim()
      .required("Service Type is required"),
    auth_format: Yup.string()
      .trim()
      .required("Credential Format is required"),
    auth_endpoint: Yup.string()
      .url("Must be a valid URL")
      .required("Authorization Endpoint is required"),
    token_endpoint: Yup.string()
      .url("Must be a valid URL")
      .required("Token Endpoint is required"),
    userinfo_endpoint: Yup.string()
      .url("Must be a valid URL")
      .required("Userinfo Endpoint is required"),
    token_auth_method: Yup.string()
      .required("Token Authentication Method is required"),
    scopes_path: Yup.string()
      .required("Scope configuration is required"),
    client_id: Yup.string()
      .trim()
      .required("Client ID is required"),
    client_secret: Yup.string()
      .trim()
      .required("Client Secret is required"),
  });

  const formik = useFormik({
    initialValues: {
      provider_id: "",
      name: "",
      service_type: "",
      auth_format: "raw_oauth2",
      token_auth_method: "",
      auth_endpoint: "",
      token_endpoint: "",
      redirect_uri: `${apiUrl}/oauth/callback`,
      userinfo_endpoint: "",
      scopes_path: "",
      icon_path: "",
      enabled: true,
      client_id: "",
      client_secret: "",
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: handleSubmit,
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    submitCount,
  } = formik;

  async function handleSubmit(formValues) {
    if (providerDraft.hasPendingEditorTransaction) {
      setError("Apply or discard the open scopes changes before creating the provider.");
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      const providerData = {
        provider_id: formValues.provider_id,
        name: formValues.name,
        service_type: formValues.service_type,
        auth_format: formValues.auth_format,
        oauth_config: {
          auth_endpoint: formValues.auth_endpoint,
          token_endpoint: formValues.token_endpoint,
          token_auth_method: formValues.token_auth_method,
          redirect_uri: formValues.redirect_uri,
          userinfo_endpoint: formValues.userinfo_endpoint,
        },
        enabled: formValues.enabled,
      };

      await createProviderFromDraft(providerData, {
        scopes: providerDraft.scopesDraft,
        clientId: formValues.client_id,
        clientSecret: formValues.client_secret,
        icon: providerDraft.iconFile,
      });

      queryClient.invalidateQueries({ queryKey: ['get-providers', orgId] });
      queryClient.invalidateQueries({ queryKey: ['get-agents', { org_id: orgId }] });
      
      if (settingsMode) {
        navigate(ROUTES.SETTINGS.FORGE_PROVIDERS);
      } else {
        navigate(ROUTES.FORGE.ADD_AGENT, {
          state: {
            preselectedProvider: formValues.provider_id,
            providerName: formValues.name
          }
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || "Failed to create provider");
      setIsSubmitting(false);
    }
  }

  const hasUploadingFiles = false; // Can be extended if file uploads need tracking
  const isDisabled = isSubmitting || hasUploadingFiles;

  return (
    <div className="w-full">
      {settingsMode
        ? <ModelsHeader title="Forge Providers" onBack={() => navigate(returnRoute)} />
        : <Header type="forge" />}
      <div className="h-[calc(100%-80px)] overflow-auto show-scrollbar">
        <div className="max-w-7xl mx-auto px-8 py-6">
          {/* Main Content Container */}
          <div className="bg-surface-panel dark:bg-surface-panel rounded-lg shadow-sm border border-edge-gray-200 dark:border-edge-gray-700 p-8">
            {/* Header section */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-lg font-semibold text-ink-ink dark:text-ink-white">
                  Add Provider
                </h1>
                <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mt-1">
                  Configure a new OAuth provider for your organization
                </p>
              </div>
              <div className="flex items-center gap-3">
                <SecondaryButton
                  type="button"
                  onClick={() => navigate(returnRoute)}
                  className="px-4 py-2"
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  form="add-provider-form"
                  onClick={(event) => {
                    if (providerDraft.hasPendingEditorTransaction) {
                      event.preventDefault();
                      setError("Apply or discard the open scopes changes before creating the provider.");
                    }
                  }}
                  disabled={isDisabled || isSubmitting}
                  className="px-6 py-2"
                >
                  {isSubmitting ? "Creating..." : "Create Provider"}
                </PrimaryButton>
              </div>
            </div>

            <hr className="border-edge-gray-200 dark:border-edge-gray-700 mb-8" />

            {error && <AlertBanner className="mb-6">{error}</AlertBanner>}

            <form id="add-provider-form" onSubmit={formik.handleSubmit}>
              <div className="space-y-8">
                {/* Section 1: Basic Information */}
                <div>
                  <h3 className={SECTION_HEADING_CLASS}>Basic Information</h3>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Provider ID <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          name="provider_id"
                          value={values.provider_id}
                          onChange={(e) => {
                            // Strip spaces and convert to lowercase as user types
                            const sanitized = e.target.value.replace(/\s/g, '').toLowerCase();
                            setFieldValue('provider_id', sanitized);
                          }}
                          onBlur={handleBlur}
                          placeholder="e.g., google_workspace"
                          error={Boolean(errors.provider_id && (touched.provider_id || submitCount > 0))}
                        />
                        <p className={HELP_TEXT_CLASS}>Unique identifier</p>
                        {errors.provider_id && (touched.provider_id || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.provider_id}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Display Name <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="e.g., Google Workspace"
                          error={Boolean(errors.name && (touched.name || submitCount > 0))}
                        />
                        {errors.name && (touched.name || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.name}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Service Type <span className="text-ink-red-500">*</span>
                          <FormInfo content="Internal identifier used to namespace and retrieve this provider's tokens." />
                        </label>
                        <Input
                          type="text"
                          name="service_type"
                          value={values.service_type}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="e.g., google"
                          error={Boolean(errors.service_type && (touched.service_type || submitCount > 0))}
                        />
                        {errors.service_type && (touched.service_type || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.service_type}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Auth Format and Icon */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Credential Format <span className="text-ink-red-500">*</span>
                          <FormInfo content="Determines how provider credentials are handled, such as raw OAuth2, Google SDK, or Microsoft MSAL." />
                        </label>
                        <Input
                          type="text"
                          name="auth_format"
                          value={values.auth_format}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="e.g., raw_oauth2, google_sdk"
                          error={Boolean(errors.auth_format && (touched.auth_format || submitCount > 0))}
                        />
                        <p className={HELP_TEXT_CLASS}>
                          e.g., raw_oauth2, google_sdk, microsoft_msal
                        </p>
                        {errors.auth_format && (touched.auth_format || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.auth_format}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Icon
                        </label>
                        <FileUploadInput
                          orgId={orgId}
                          uploadType="provider_icon"
                          entityId={values.provider_id}
                          accept=".svg,.png,.jpg,.jpeg,.webp"
                          placeholder="Drop icon or click to browse"
                          deferUpload
                          onFileSelected={providerDraft.selectIcon}
                        />
                        <p className={HELP_TEXT_CLASS}>SVG, PNG, JPG or WebP</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: OAuth Configuration */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <h3 className={SECTION_HEADING_CLASS}>OAuth Configuration</h3>
                  <div className="space-y-6">
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Redirect URI
                        <FormInfo content="Where the provider returns users after authorization. Copy this exact value into the OAuth app settings." />
                      </label>
                      <Input
                        type="url"
                        name="redirect_uri"
                        value={values.redirect_uri}
                        disabled
                      />
                      <p className={HELP_TEXT_CLASS}>Auto-configured. Copy to your OAuth app settings.</p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Authorization Endpoint <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="url"
                          name="auth_endpoint"
                          value={values.auth_endpoint}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="https://accounts.google.com/o/oauth2/v2/auth"
                          error={Boolean(errors.auth_endpoint && (touched.auth_endpoint || submitCount > 0))}
                        />
                        {errors.auth_endpoint && (touched.auth_endpoint || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.auth_endpoint}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Token Endpoint <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="url"
                          name="token_endpoint"
                          value={values.token_endpoint}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="https://oauth2.googleapis.com/token"
                          error={Boolean(errors.token_endpoint && (touched.token_endpoint || submitCount > 0))}
                        />
                        {errors.token_endpoint && (touched.token_endpoint || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.token_endpoint}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Userinfo Endpoint <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="url"
                          name="userinfo_endpoint"
                          value={values.userinfo_endpoint}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="https://www.googleapis.com/oauth2/v3/userinfo"
                          error={Boolean(errors.userinfo_endpoint && (touched.userinfo_endpoint || submitCount > 0))}
                        />
                        {errors.userinfo_endpoint && (touched.userinfo_endpoint || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.userinfo_endpoint}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Token Authentication Method <span className="text-ink-red-500">*</span>
                        <FormInfo content="Controls whether client credentials are sent in the token request body or the Authorization header." />
                      </label>
                      <Select
                        value={values.token_auth_method}
                        onChange={(val) => setFieldValue("token_auth_method", val)}
                        placeholder="Select authentication method"
                        error={!!(errors.token_auth_method && (touched.token_auth_method || submitCount > 0))}
                        options={[
                          { value: "form",  label: "Form-based", description: "Client credentials in body (Google, HubSpot)" },
                          { value: "basic", label: "Basic Auth",  description: "Credentials in header (ZoomInfo, Okta, Auth0)" },
                        ]}
                      />
                      {errors.token_auth_method && (touched.token_auth_method || submitCount > 0) && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.token_auth_method}</p>
                      )}
                      <p className={HELP_TEXT_CLASS}>
                        How client credentials are sent to the token endpoint
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 3: App Credentials */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <h3 className="text-sm font-semibold text-ink-gray-900 dark:text-ink-white mb-1">App Credentials</h3>
                  <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mb-4">
                    Stored encrypted — never visible after saving.
                  </p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Client ID <span className="text-ink-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        name="client_id"
                        value={values.client_id}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="OAuth app Client ID"
                        autoComplete="off"
                        error={Boolean(errors.client_id && (touched.client_id || submitCount > 0))}
                      />
                      {errors.client_id && (touched.client_id || submitCount > 0) && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.client_id}</p>
                      )}
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Client Secret <span className="text-ink-red-500">*</span>
                      </label>
                      <Input
                        type="password"
                        name="client_secret"
                        value={values.client_secret}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="OAuth app Client Secret"
                        autoComplete="new-password"
                        error={Boolean(errors.client_secret && (touched.client_secret || submitCount > 0))}
                      />
                      {errors.client_secret && (touched.client_secret || submitCount > 0) && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.client_secret}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section 4: Scopes */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <h3 className={SECTION_HEADING_CLASS}>Configuration Details</h3>
                  <div>
                    <label className={FIELD_LABEL_CLASS}>
                      Scopes <span className="text-ink-red-500">*</span>
                      <FormInfo content="JSON configuration defining the OAuth permissions this provider requests." />
                    </label>
                    <div className="flex items-center gap-2">
                      <FileUploadInput
                        orgId={orgId}
                        uploadType="scopes"
                        entityId={values.provider_id}
                        accept=".json"
                        placeholder="Drop scopes.json or click to browse"
                        deferUpload
                        onFileSelected={async (file) => {
                          const accepted = await providerDraft.selectScopesFile(file);
                          if (accepted) setFieldValue("scopes_path", "draft");
                        }}
                        className={`flex-1 ${
                          errors.scopes_path && submitCount > 0 ? "border-edge-red-500" : ""
                        }`}
                      />
                      {!providerDraft.isEditingScopes && (
                        <CompactButton
                          type="button"
                          onClick={providerDraft.openScopesEditor}
                          disabled={!values.provider_id}
                          title={!values.provider_id ? "Enter Provider ID first" : "Create scopes JSON inline"}
                        >
                          <Pencil className="w-4 h-4" />
                          {values.scopes_path ? "Edit" : "Create"}
                        </CompactButton>
                      )}
                    </div>
                    <p className={HELP_TEXT_CLASS}>JSON file with OAuth scopes</p>
                    {providerDraft.scopesError && <p className="text-ink-red-500 text-xs mt-1">{providerDraft.scopesError}</p>}
                    {errors.scopes_path && submitCount > 0 && (
                      <p className="text-ink-red-500 text-xs mt-1">{errors.scopes_path}</p>
                    )}
                  </div>
                </div>

                {/* Section 4: Status */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-3">
                      <Toggle checked={values.enabled} onChange={(checked) => setFieldValue("enabled", checked)} />
                      <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                        Published
                      </label>
                    </div>
                    <p className="text-[11px] text-ink-gray-500 dark:text-ink-gray-400">
                      Unpublished providers accept no connections and their agents stay out of service.
                    </p>
                  </div>
                </div>

                {/* Scopes JSON Editor (shown when creating/editing) */}
                {providerDraft.isEditingScopes && (
                  <FileEditorSection
                    fileType="scopes"
                    fileContent={providerDraft.scopesBuffer}
                    originalFileContent={providerDraft.scopesBufferBaseline}
                    onChange={providerDraft.updateScopesBuffer}
                    onSave={() => {
                      if (providerDraft.applyScopes()) setFieldValue("scopes_path", "draft");
                    }}
                    onCancel={providerDraft.discardScopes}
                    saveLabel="Apply"
                    cancelLabel="Discard"
                    jsonError={providerDraft.scopesError}
                    fileChangeDetection={{}}
                  />
                )}              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProviderPage;
